import React, { useRef, useCallback } from 'react';
import Handsontable from 'handsontable/base';
import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import { cloneDeep, isEqual, capitalize, set } from 'lodash';

// Types
import { TranslationFunction } from '../../../components/i18n/types';

// Utils
import {
    getStartCol,
    isSelectingRefColHeader,
    isSelectingRefRowHeader,
    moveCellsDown,
    moveCellsRight,
    getStartRow,
    getEndRow,
    getEndCol,
    getFirstSelection,
    isSelectingMultipleSelections,
    unselectedRowCount,
    unselectedColCount,
    getAllCellsBetween,
    getNRowsInSelection,
    getNColsInSelection,
    excludeRefHeadersFromSelection,
    isSelectingSingleSelection,
} from '../utils/tableCellSelectionUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import { cellIsReadOnly, updateCellType as updateCellTypeUtil } from '../utils/tableCellTypeUtils';
import { createTableCellEntry } from '../../../../common/table/utils/tableInitialisationUtils';
import getSelectionStateForContent from '../../../components/editor/customRichUtils/selection/getSelectionStateForContent';
import { getCellTextAlignment } from '../utils/tableCellFormattingUtils';
import { getShortcutHtml } from '../utils/tableContextMenuUtils';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import { isBackgroundStyle, isColorStyle } from '../../../components/editor/editorUtils';
import {
    parseCellContentString,
    stringifyCellContent,
} from '../../../../common/table/utils/tableCellContentStringUtils';

// Constants
import {
    TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT,
    TABLE_MIN_COL_COUNT,
    TABLE_MIN_ROW_COUNT,
} from '../../../../common/table/tableConstants';
import { CellData, CellSelections, CellTypeObject } from '../../../../common/table/TableTypes';
import { CellTypeNames, TextAlignment, VerticalAlignment } from '../../../../common/table/CellTypeConstants';
import { TABLE_ELEMENT_CELL_SELECTIONS_UPDATE } from '../../../../common/elements/selectionConstants';
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../components/editor/plugins/textColor/textColorConstants';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';

interface UseTableOperationsProps {
    elementId: string;
    locale: string;
    currencyPreference: string;
    gridSize: number;
    showTitle: boolean;
    isSingleSelected: boolean;
    isCurrentlyEditingTableCell: boolean;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    translateTableContextMenu: TranslationFunction;
    jumpToTitle: () => void;
    setCurrentlyEditingCellEditorCoords: (editorFocusRect: Rect | null) => void;
    dispatchStartEditingTitle: (elementId: string) => void;
    dispatchStartEditingTableCell: (elementId: string, cellRect: DOMRect | undefined) => void;
    dispatchStartEditingTableGrid: () => void;
    dispatchFinishEditingElement: () => void;
    dispatchUpdateTableElement: (args: object) => void;
}

export interface TableOperations {
    getHotTableData: () => CellData[][] | undefined;
    getHotCurrentCellSelections: () => CellSelections | undefined;
    getHotColWidthsGU: () => number[] | undefined;
    renderAndResize: () => void;
    popTransactionIdForNextUpdateCellSelection: () => number | undefined;
    updateHotCellSelections: (cellSelections: CellSelections, transactionId?: number) => void;
    updateCellType: (cellType: CellTypeNames | CellTypeObject, cellSelections?: CellSelections) => void;
    updateCellTypeFormat: (typeFormatChanges: CellTypeObject) => void;
    updateColWidthPx: (column: number, newColWidthPx: number) => void;
    updateColWidthsPx: (newColWidthsPx: number[]) => void;
    addRowsAboveMenuName: () => string;
    addRowsAboveDisabled: () => boolean;
    addRowsAbove: () => void;
    addRowsBelowMenuName: () => string;
    addRowsBelowDisabled: () => boolean;
    addRowsBelow: () => void;
    addColsLeftMenuName: () => string;
    addColsLeftDisabled: () => boolean;
    addColsLeft: () => void;
    addColsRightMenuName: () => string;
    addColsRightDisabled: () => boolean;
    addColsRight: () => void;
    removeRowsMenuName: () => string;
    removeRowsDisabled: () => boolean;
    removeRows: () => void;
    removeColsMenuName: () => string;
    removeColsDisabled: () => boolean;
    removeCols: () => void;
    startEditingCellEditor: (cellCoords: { row: number; col: number }) => void;
    finishEditingCellEditor: () => void;
    addFormulaToCell: () => void;
    setCellBackground: (color: string) => void;
    getCellBackground: (currentCellSelections: CellSelections) => string | null | undefined;
    setTempColor: (color: string) => void;
    hasInlineStyle: (cellData: CellData, inlineStyle: string) => boolean;
    applyTextStyle: (textStyle: string | null) => void;
    textAlignmentMenuName: (textAlignment: TextAlignment) => () => string;
    verticalAlignmentMenuName: (verticalAlignment: VerticalAlignment) => () => string;
    updateCellTextAlignment: (textAlignment: TextAlignment) => () => void;
    updateCellVerticalAlignment: (verticalAlignment: VerticalAlignment) => () => void;
    jumpToTitle: () => void;
    setCellData: (
        cellDataChanges: Array<[number, number, (cellData: CellData, row?: number, col?: number) => CellData]>,
        source?: string,
        transactionId?: number,
    ) => void;
    setCellDataForCellSelections: (
        getCellDataUpdatesFn: (cellData: CellData, row?: number, col?: number) => CellData,
        source: string,
        cellSelections?: CellSelections,
    ) => void;
    setCellMetaObject: (row: number, col: number, cellMeta: Partial<Handsontable.CellMeta>) => void;
}

export const useTableOperations = (props: UseTableOperationsProps): TableOperations => {
    const {
        elementId,
        locale,
        currencyPreference,
        gridSize,
        showTitle,
        isSingleSelected,
        isCurrentlyEditingTableCell,
        hotTableInstanceRef,
        translateTableContextMenu,
        setCurrentlyEditingCellEditorCoords,
        dispatchStartEditingTitle,
        dispatchUpdateTableElement,
        dispatchStartEditingTableCell,
        dispatchStartEditingTableGrid,
    } = props;

    const transactionIdForNextUpdateCellSelectionRef = useRef<number | undefined>();

    const getHotTableData = useCallback((): CellData[][] | undefined => {
        if (!hotTableInstanceRef.current) return;

        const cellMetas = hotTableInstanceRef.current.getCellsMeta();
        const data: CellData[][] = [];

        const rowCount = hotTableInstanceRef.current.countRows();
        const colCount = hotTableInstanceRef.current.countCols();

        cellMetas.forEach((cellMeta) => {
            const { row, col, cellData } = cellMeta || {};
            if (cellData && row < rowCount && col < colCount) set(data, [row, col], cellData);
        });

        return data;
    }, [hotTableInstanceRef]);

    const getHotCurrentCellSelections = useCallback((): CellSelections | undefined => {
        return hotTableInstanceRef.current?.getSelected();
    }, [hotTableInstanceRef]);

    const getHotColWidthsGU = useCallback((): number[] | undefined => {
        if (!hotTableInstanceRef.current) return;

        const colWidthsGU: number[] = [];
        for (let col = 0; col < hotTableInstanceRef.current.countCols(); col++) {
            const isLastCol = col === hotTableInstanceRef.current.countCols() - 1;
            const offset = isLastCol ? 2 : 0;

            const width = (hotTableInstanceRef.current.getColWidth(col) + offset) / gridSize;

            colWidthsGU.push(width);
        }

        return colWidthsGU;
    }, [hotTableInstanceRef, gridSize]);

    // **********************************
    // TABLE RENDER
    // **********************************

    const renderAndResize = useCallback((): void => {
        // `requestAnimationFrame` is used for calling render here as it improves performance
        requestAnimationFrame(() => {
            if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

            // Render recent changes
            hotTableInstanceRef.current.render();

            // Ensure row heights are correct
            hotTableInstanceRef.current.refreshDimensions();
        });
    }, [hotTableInstanceRef]);

    // **********************************
    // CELL SELECTIONS
    // **********************************

    const popTransactionIdForNextUpdateCellSelection = useCallback((): number | undefined => {
        const transactionId = transactionIdForNextUpdateCellSelectionRef.current;
        transactionIdForNextUpdateCellSelectionRef.current = undefined;
        return transactionId;
    }, []);

    const updateHotCellSelections = useCallback(
        (cellSelections: CellSelections, transactionId?: number): void => {
            if (!isSingleSelected) return;

            const newCellSelections = asObject(cellSelections) as CellSelections;
            if (!hotTableInstanceRef.current) return;

            hotTableInstanceRef.current.deselectCell();

            if (!newCellSelections) return;

            const currentHotCellSelections = hotTableInstanceRef.current.getSelected();
            if (isEqual(newCellSelections, currentHotCellSelections)) return;

            transactionIdForNextUpdateCellSelectionRef.current = transactionId;

            // Prioritise on selecting the first ref column/row header if it exists in cellSelections.
            // - This is because we can't select ref headers using hot.selectCells() function. So we are not able to
            //   select ref headers and normal cells at the same time. Therefore, need to prioritise selecting one.

            const selectingRefColHeader = cellSelections.find(isSelectingRefColHeader);
            if (selectingRefColHeader) {
                hotTableInstanceRef.current.selectColumns(
                    Math.max(getStartCol(selectingRefColHeader), 0),
                    Math.min(getEndCol(selectingRefColHeader), hotTableInstanceRef.current.countCols() - 1),
                );
                return;
            }

            const selectingRefRowHeader = cellSelections.find(isSelectingRefRowHeader);
            if (selectingRefRowHeader) {
                hotTableInstanceRef.current.selectRows(
                    Math.max(getStartRow(selectingRefRowHeader), 0),
                    Math.min(getEndRow(selectingRefRowHeader), hotTableInstanceRef.current.countRows() - 1),
                );
                return;
            }

            hotTableInstanceRef.current.selectCells(newCellSelections);
        },
        [isSingleSelected, hotTableInstanceRef],
    );

    // **********************************
    // HELPER FUNCTIONS
    // **********************************

    const setCellMetaObject = useCallback(
        (row: number, col: number, cellMeta: Partial<Handsontable.CellMeta>): void => {
            if (!hotTableInstanceRef.current) return;

            const newCellMeta = cloneDeep(cellMeta);
            if (newCellMeta.cellData) {
                newCellMeta.readOnly = cellIsReadOnly(newCellMeta.cellData);
            }

            hotTableInstanceRef.current.setCellMetaObject(row, col, newCellMeta);
        },
        [hotTableInstanceRef],
    );

    const setCellData = useCallback(
        (
            cellDataChanges: Array<[number, number, (cellData: CellData, row?: number, col?: number) => CellData]>,
            source = 'CellMeta.update',
            transactionId?: number,
        ): void => {
            if (!hotTableInstanceRef.current) return;

            const hotTableData = getHotTableData();
            const hotColWidthsGU = getHotColWidthsGU();
            if (!hotTableData || !hotColWidthsGU) return;

            const newData = cloneDeep(hotTableData);

            hotTableInstanceRef.current.suspendRender();

            const referencedCellUpdates: Array<[number, number, Handsontable.CellValue]> = [];
            const sheet = hyperFormulaInstance.getSheetId(elementId);

            cellDataChanges.forEach((change) => {
                const [row, col, getCellDataUpdatesFn] = change;

                const cellDataUpdates = getCellDataUpdatesFn(newData[row][col], row, col);

                newData[row][col] = {
                    ...newData[row][col],
                    ...cellDataUpdates,
                };

                setCellMetaObject(row, col, { cellData: newData[row][col] });

                // If the current cell has dependants, we need to update the values so that afterChange can run
                if (sheet !== undefined) {
                    const cellDependents = hyperFormulaInstance.getCellDependents({ sheet, row, col });
                    if (cellDependents.length) referencedCellUpdates.push([row, col, newData[row][col].value]);
                }
            });

            hotTableInstanceRef.current.resumeRender();
            renderAndResize();

            const undoRedoCellSelectionAction = {
                type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                id: elementId,
                cellSelections: hotTableInstanceRef.current.getSelected(),
            };

            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    tableContent: {
                        colWidthsGU: hotColWidthsGU,
                        data: newData,
                    },
                },

                // On undo/redo, make sure to go to previous cell selections
                batchUndoActions: [undoRedoCellSelectionAction],
                batchRedoActions: [undoRedoCellSelectionAction],

                transactionId,
            });

            if (referencedCellUpdates.length === 0) return;

            // Update cells that are referenced so that cell type changes can be applied to the dependents
            // This must run after the table element has been updated, because any dependent cell type
            // changes through setDataAtCell > afterChange will also be updated through this function recursively,
            // and we don't want the inner table data updates to happen before the original one
            //
            // There is a Handsontable bug where setDataAtCell only resolves source properly if passed as 4th param,
            // despite the type overloads saying otherwise
            // TODO-HANDSONTABLE-UPGRADE: Check whether this is still a bug after upgrading Handsontable
            hotTableInstanceRef.current?.setDataAtCell(referencedCellUpdates, undefined, undefined, source);
        },
        [
            hotTableInstanceRef,
            getHotTableData,
            getHotColWidthsGU,
            elementId,
            setCellMetaObject,
            renderAndResize,
            dispatchUpdateTableElement,
        ],
    );

    const setCellDataForCellSelections = useCallback(
        (
            getCellDataUpdatesFn: (cellData: CellData, row?: number, col?: number) => CellData,
            source: string,
            cellSelections = getHotCurrentCellSelections(),
        ): void => {
            const cellDataChanges = getAllCellsBetween(cellSelections).map<
                [number, number, (cellData: CellData) => CellData]
            >((cell) => {
                const { row, col } = cell;

                return [row, col, getCellDataUpdatesFn];
            });

            return setCellData(cellDataChanges, source);
        },
        [getHotCurrentCellSelections, setCellData],
    );

    // **********************************
    // CELL TYPE
    // **********************************

    const updateCellType = useCallback(
        (cellType: CellTypeNames | CellTypeObject, cellSelections?: CellSelections): void => {
            const hot = hotTableInstanceRef.current;

            if (typeof cellType === 'string') {
                const updateCellTypeFn = (cellData: CellData, row?: number, col?: number) =>
                    updateCellTypeUtil(cellData, cellType, currencyPreference, elementId, row, col, hot);

                setCellDataForCellSelections(updateCellTypeFn, 'CellMeta.updateCellType', cellSelections);

                return;
            }

            setCellDataForCellSelections(
                (cellData) => ({ ...cellData, type: cellType }),
                'CellMeta.updateCellType',
                cellSelections,
            );
        },
        [hotTableInstanceRef, currencyPreference, elementId, setCellDataForCellSelections],
    );

    const updateCellTypeFormat = useCallback(
        (typeFormatChanges: CellTypeObject): void => {
            setCellDataForCellSelections(
                (cellData) => ({
                    ...cellData,
                    type: { ...cellData.type, ...typeFormatChanges },
                }),
                'CellMeta.updateCellTypeFormat',
            );
        },
        [setCellDataForCellSelections],
    );

    // **********************************
    // Update Column Width
    // **********************************

    const updateColWidthPx = useCallback(
        (column: number, newColWidthPx: number): void => {
            if (!hotTableInstanceRef.current) return;

            const manualColumnResizePluginInstance =
                hotTableInstanceRef.current.getPlugin<'manualColumnResize'>('manualColumnResize');
            const newSize = manualColumnResizePluginInstance.setManualSize(column, newColWidthPx);

            // This is a hack to force the internal state of the manualColumnResize plugin to contain the size
            // that corresponds to our grid unit
            manualColumnResizePluginInstance.newSize = newSize;
        },
        [hotTableInstanceRef],
    );

    const updateColWidthsPx = useCallback(
        (newColWidthsPx: number[]): void => {
            if (!hotTableInstanceRef.current) return;

            let isUpdated = false;
            newColWidthsPx.forEach((colWidthPx, index) => {
                const currColWidth = hotTableInstanceRef.current?.getColWidth(index);

                if (currColWidth === colWidthPx) return;

                updateColWidthPx(index, colWidthPx);
                isUpdated = true;
            });

            if (isUpdated)
                requestAnimationFrame(() => {
                    if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

                    hotTableInstanceRef.current.render();
                });
        },
        [hotTableInstanceRef, updateColWidthPx],
    );

    // **********************************
    // ADD ROWS ABOVE
    // **********************************

    const addRowsAboveMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleRow =
            firstCellSelection &&
            (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                isSelectingRefColHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('addRowAbove_one', 'Insert row above');
        const labelPlural = translateTableContextMenu('addRowAbove_other', 'Insert rows above');
        const label = isSingleRow ? labelSingle : labelPlural;
        return `${label} ${getShortcutHtml(['alt', '↑'])}`;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const addRowsAboveDisabled = useCallback((): boolean => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        return isSelectingMultipleSelections(hotCurrentCellSelections);
    }, [getHotCurrentCellSelections]);

    const addRowsAbove = useCallback((): void => {
        if (addRowsAboveDisabled()) return;

        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `addRowsAbove:${transactionId}`;

        transactionIdForNextUpdateCellSelectionRef.current = transactionId;

        const hotCurrentCellSelections = getHotCurrentCellSelections();
        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) {
            // If no cell selection, add row to the start
            hotTableInstanceRef.current.alter('insert_row_above', undefined, undefined, source);
            return;
        }

        const isRefColHeaderSelected = isSelectingRefColHeader(firstCellSelection);
        if (isRefColHeaderSelected) {
            hotTableInstanceRef.current.alter('insert_row_above', 0, 1, source);

            updateHotCellSelections(
                [[0, getStartCol(firstCellSelection), 0, getEndCol(firstCellSelection)]],
                transactionId,
            );
            return;
        }

        const index = Math.min(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
        const amount = getNRowsInSelection(firstCellSelection);

        hotTableInstanceRef.current.alter('insert_row_above', index, amount, source);

        updateHotCellSelections([asObject(firstCellSelection)], transactionId);
    }, [addRowsAboveDisabled, hotTableInstanceRef, getHotCurrentCellSelections, updateHotCellSelections]);

    // **********************************
    // ADD ROWS BELOW
    // **********************************

    const addRowsBelowMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleRow =
            firstCellSelection &&
            (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                isSelectingRefColHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('addRowBelow_one', 'Insert row below');
        const labelPlural = translateTableContextMenu('addRowBelow_other', 'Insert rows below');
        const label = isSingleRow ? labelSingle : labelPlural;
        return `${label} ${getShortcutHtml(['alt', '↓'])}`;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const addRowsBelowDisabled = useCallback((): boolean => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        return isSelectingMultipleSelections(hotCurrentCellSelections);
    }, [getHotCurrentCellSelections]);

    const addRowsBelow = useCallback((): void => {
        if (addRowsBelowDisabled()) return;

        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `addRowsBelow:${transactionId}`;

        transactionIdForNextUpdateCellSelectionRef.current = transactionId;
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        if (!hotCurrentCellSelections) {
            hotTableInstanceRef.current.alter('insert_row_below', undefined, undefined, source);
            return;
        }

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) return;

        const isRefColHeaderSelected = isSelectingRefColHeader(firstCellSelection);
        if (isRefColHeaderSelected) {
            const index = getNRowsInSelection(firstCellSelection) - 1;
            const amount = 1;

            hotTableInstanceRef.current.alter('insert_row_below', index, amount, source);
        } else {
            const index = Math.max(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
            const amount = getNRowsInSelection(firstCellSelection);

            hotTableInstanceRef.current.alter('insert_row_below', index, amount, source);
        }

        updateHotCellSelections([moveCellsDown(firstCellSelection)], transactionId);
    }, [addRowsBelowDisabled, hotTableInstanceRef, getHotCurrentCellSelections, updateHotCellSelections]);

    // **********************************
    // ADD COLS LEFT
    // **********************************

    const addColsLeftMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleCol =
            firstCellSelection &&
            (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                isSelectingRefRowHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('addColumnLeft_one', 'Insert column left');
        const labelPlural = translateTableContextMenu('addColumnLeft_other', 'Insert columns left');
        const label = isSingleCol ? labelSingle : labelPlural;
        return `${label} ${getShortcutHtml(['alt', '←'])}`;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const addColsLeftDisabled = useCallback((): boolean => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();
        return isSelectingMultipleSelections(hotCurrentCellSelections);
    }, [getHotCurrentCellSelections]);

    const addColsLeft = useCallback((): void => {
        if (addColsLeftDisabled()) return;

        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `addColsLeft:${transactionId}`;

        transactionIdForNextUpdateCellSelectionRef.current = transactionId;
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) {
            // If no cell selection, add col to the start
            hotTableInstanceRef.current.alter('insert_col_start', undefined, undefined, source);
            return;
        }

        const isRefRowHeaderSelected = isSelectingRefRowHeader(firstCellSelection);
        if (isRefRowHeaderSelected) {
            const index = 0;
            const amount = 1;

            hotTableInstanceRef.current.alter('insert_col_start', index, amount, source);
            updateHotCellSelections(
                [[getStartRow(firstCellSelection), 0, getEndRow(firstCellSelection), 0]],
                transactionId,
            );
            return;
        }

        const index = Math.min(getStartCol(firstCellSelection), getEndCol(firstCellSelection));
        const amount = getNColsInSelection(firstCellSelection);

        hotTableInstanceRef.current.alter('insert_col_start', index, amount, source);

        updateHotCellSelections([asObject(firstCellSelection)], transactionId);
    }, [addColsLeftDisabled, hotTableInstanceRef, getHotCurrentCellSelections, updateHotCellSelections]);

    // **********************************
    // ADD COLS RIGHT
    // **********************************

    const addColsRightMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleCol =
            firstCellSelection &&
            (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                isSelectingRefRowHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('addColumnRight_one', 'Insert column right');
        const labelPlural = translateTableContextMenu('addColumnRight_other', 'Insert columns right');
        const label = isSingleCol ? labelSingle : labelPlural;
        return `${label} ${getShortcutHtml(['alt', '→'])}`;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const addColsRightDisabled = useCallback((): boolean => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        return isSelectingMultipleSelections(hotCurrentCellSelections);
    }, [getHotCurrentCellSelections]);

    const addColsRight = useCallback((): void => {
        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `addColsRight:${transactionId}`;
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        if (!hotCurrentCellSelections) {
            hotTableInstanceRef.current.alter('insert_col_end', undefined, undefined, source);
            return;
        }

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) return;

        const isRefRowHeaderSelected = isSelectingRefRowHeader(firstCellSelection);
        if (isRefRowHeaderSelected) {
            const index = getNColsInSelection(firstCellSelection) - 1;
            const amount = 1;

            hotTableInstanceRef.current.alter('insert_col_start', index, amount, source);
        } else {
            const index = Math.max(getStartCol(firstCellSelection), getEndCol(firstCellSelection)) + 1;
            const amount = getNColsInSelection(firstCellSelection);

            hotTableInstanceRef.current.alter('insert_col_start', index, amount, source);
        }

        updateHotCellSelections([moveCellsRight(firstCellSelection)], transactionId);
    }, [hotTableInstanceRef, getHotCurrentCellSelections, updateHotCellSelections]);

    // **********************************
    // REMOVE ROWS
    // **********************************

    const removeRowsMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleRow =
            firstCellSelection &&
            (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                isSelectingRefColHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('removeRow_one', 'Remove row');
        const labelPlural = translateTableContextMenu('removeRow_other', 'Remove rows');
        const label = isSingleRow ? labelSingle : labelPlural;
        return label;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const removeRowsDisabled = useCallback((): boolean => {
        const hotTableData = getHotTableData();
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        if (!hotTableData || isSelectingMultipleSelections(hotCurrentCellSelections)) return true;

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) return true;

        return unselectedRowCount(hotTableData, firstCellSelection) < TABLE_MIN_ROW_COUNT;
    }, [getHotTableData, getHotCurrentCellSelections]);

    const removeRows = useCallback((): void => {
        if (removeRowsDisabled()) return;

        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `removeRows:${transactionId}`;

        transactionIdForNextUpdateCellSelectionRef.current = transactionId;
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) return;

        const index = Math.min(getStartRow(firstCellSelection), getEndRow(firstCellSelection));
        const amount = getNRowsInSelection(firstCellSelection);

        hotTableInstanceRef.current.alter('remove_row', index, amount, source);
    }, [removeRowsDisabled, hotTableInstanceRef, getHotCurrentCellSelections]);

    // **********************************
    // REMOVE COLS
    // **********************************

    const removeColsMenuName = useCallback((): string => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isSingleCol =
            firstCellSelection &&
            (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                isSelectingRefRowHeader(firstCellSelection));

        const labelSingle = translateTableContextMenu('removeColumn_one', 'Remove column');
        const labelPlural = translateTableContextMenu('removeColumn_other', 'Remove columns');
        const label = isSingleCol ? labelSingle : labelPlural;
        return label;
    }, [getHotCurrentCellSelections, translateTableContextMenu]);

    const removeColsDisabled = useCallback((): boolean => {
        const hotTableData = getHotTableData();
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!hotTableData || !firstCellSelection) return true;

        return (
            isSelectingMultipleSelections(hotCurrentCellSelections) ||
            unselectedColCount(hotTableData, firstCellSelection) < TABLE_MIN_COL_COUNT
        );
    }, [getHotTableData, getHotCurrentCellSelections]);

    const removeCols = useCallback((): void => {
        if (removeColsDisabled()) return;

        if (!hotTableInstanceRef.current) return;

        const transactionId = getNewTransactionId();
        const source = `removeCols:${transactionId}`;

        transactionIdForNextUpdateCellSelectionRef.current = transactionId;
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!firstCellSelection) return;

        const index = Math.min(getStartCol(firstCellSelection), getEndCol(firstCellSelection));
        const amount = getNColsInSelection(firstCellSelection);

        hotTableInstanceRef.current.alter('remove_col', index, amount, source);
    }, [removeColsDisabled, hotTableInstanceRef, getHotCurrentCellSelections]);

    // **********************************
    // CELL EDITING
    // **********************************

    const startEditingCellEditor = useCallback(
        (cellCoords) => {
            setCurrentlyEditingCellEditorCoords(cellCoords);

            requestAnimationFrame(() => {
                if (!isCurrentlyEditingTableCell) {
                    // TODO: Once table editor is updated to TipTap, we can use the specific caret client coord
                    //       instead of the cell client coord
                    const cellRect = hotTableInstanceRef.current
                        ?.getCell(cellCoords.row, cellCoords.col)
                        ?.getBoundingClientRect();

                    dispatchStartEditingTableCell(elementId, cellRect);
                }
            });
        },
        [dispatchStartEditingTableCell, elementId, hotTableInstanceRef, isCurrentlyEditingTableCell],
    );

    const finishEditingCellEditor = useCallback(() => {
        setCurrentlyEditingCellEditorCoords(null);

        requestAnimationFrame(() => {
            if (isCurrentlyEditingTableCell) dispatchStartEditingTableGrid();
        });
    }, [dispatchStartEditingTableGrid, isCurrentlyEditingTableCell]);

    // **********************************
    // FORMULA
    // **********************************

    const addFormulaToCell = useCallback((): void => {
        const hotCurrentCellSelections = getHotCurrentCellSelections();

        if (!hotCurrentCellSelections || !isSelectingSingleSelection(hotCurrentCellSelections)) return;

        const cellSelection = getFirstSelection(hotCurrentCellSelections);
        if (!cellSelection) return;

        requestAnimationFrame(() => {
            if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

            hotTableInstanceRef.current?.getActiveEditor()?.beginEditing('=');
        });
    }, [hotTableInstanceRef, getHotCurrentCellSelections]);

    // **********************************
    // BACKGROUND COLOR
    // **********************************

    const setCellBackground = useCallback(
        (color: string): void => {
            setCellDataForCellSelections(
                (cellData) => ({ ...cellData, background: color }),
                'CellMeta.updateCellBackground',
            );
        },
        [setCellDataForCellSelections],
    );

    const getCellBackground = useCallback(
        (currentCellSelections: CellSelections): string | null | undefined => {
            if (!currentCellSelections) return null;

            const colors = new Set<string>();
            getAllCellsBetween(currentCellSelections).forEach((cell): void => {
                const { row, col } = cell;
                colors.add(hotTableInstanceRef.current?.getCellMeta(row, col)?.cellData?.background || null);
            });

            const values = Array.from(colors);

            // We need to return something other than null if multiple cells are selected
            if (values.length > 1) return undefined;

            // If only one cell is selected, return the color
            // otherwise return null - this is considered the first color in the color picker which
            // is the element background color
            return values.length === 1 ? values[0] : null;
        },
        [hotTableInstanceRef],
    );

    const setTempColor = useCallback(
        (color: string): void => {
            // this function is used to set the temporary cell color when the user is using the custom
            // color picker because it can update many times very quickly, for that reason we
            // are updating the hotTable instance directly to render the new colour, but not saving to redux
            if (!hotTableInstanceRef.current) return;

            const hotCurrentCellSelections = getHotCurrentCellSelections();
            if (!hotCurrentCellSelections) return;

            getAllCellsBetween(hotCurrentCellSelections).forEach((cell) => {
                if (!hotTableInstanceRef.current) return;

                const { row, col } = cell;
                const currentCellData = hotTableInstanceRef.current.getCellMeta(row, col)?.cellData || {};
                hotTableInstanceRef.current.setCellMetaObject(row, col, {
                    cellData: { ...currentCellData, background: color },
                });
            });

            hotTableInstanceRef.current.render();
        },
        [hotTableInstanceRef, getHotCurrentCellSelections],
    );

    // **********************************
    // APPLY TEXT STYLE
    // **********************************

    const hasInlineStyle = useCallback((cellData: CellData, inlineStyle: string): boolean => {
        if (cellData.textStyle?.includes(inlineStyle)) return true;

        if (!cellData.value) return false;

        const content = parseCellContentString(cellData.value);

        if (typeof content !== 'object') return false;

        const editorState = EditorState.createWithContent(convertFromRaw(content));
        return editorState.getCurrentInlineStyle().includes(inlineStyle);
    }, []);

    /**
     * Applies/removes a text style to all selected cells
     *
     * @param textStyle - The text style to apply/remove, or null to remove all color/background text styling
     */
    const applyTextStyle = useCallback(
        (textStyle: string | null): void => {
            if (!hotTableInstanceRef.current) return;

            const hotCurrentCellSelections = getHotCurrentCellSelections();
            const hotColWidthsGU = getHotColWidthsGU();
            if (!hotCurrentCellSelections || !hotColWidthsGU) return;

            const hotTableData = getHotTableData();
            if (!hotTableData) return;

            // ****************************************************
            // Determine whether to add/remove inline style based on the first cell selection

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);
            if (!firstCellSelection) return;

            // Remove any header cells from the selection
            const validCellSelection = excludeRefHeadersFromSelection(firstCellSelection);

            const cellData = hotTableData[getStartRow(validCellSelection)][getStartCol(validCellSelection)];

            const shouldAddInlineStyle = textStyle ? !hasInlineStyle(cellData, textStyle) : true;
            const isColorOrBackgroundTextStyleChange =
                !textStyle || isColorStyle(textStyle) || isBackgroundStyle(textStyle);

            // ****************************************************
            // Apply/remove inline style to all selected cells

            const newData = cloneDeep(hotTableData);

            getAllCellsBetween(hotCurrentCellSelections).forEach((cell) => {
                const { row, col } = cell;

                // Add/remove text style from cellData.textStyle

                let newTextStyle = newData[row][col].textStyle || [];
                if (shouldAddInlineStyle) {
                    newTextStyle = newTextStyle.filter((currentTextStyle) => {
                        if (isColorOrBackgroundTextStyleChange) {
                            return !(isColorStyle(currentTextStyle) || isBackgroundStyle(currentTextStyle));
                        }
                        return currentTextStyle !== textStyle;
                    });

                    textStyle && newTextStyle.push(textStyle);
                } else {
                    newTextStyle = newTextStyle.filter((style) => style !== textStyle);
                }

                newData[row][col].textStyle = newTextStyle;

                // Add/remove text style from inline style of cellData.value

                const cellValue = hotTableData[row][col].value;
                const content = cellValue && parseCellContentString(cellValue);

                if (content && typeof content === 'object') {
                    // If a color/background text style is added, remove all other existing color/background inline text styles
                    if (shouldAddInlineStyle && isColorOrBackgroundTextStyleChange) {
                        let contentState = convertFromRaw(content);
                        let newEditorState = EditorState.createWithContent(contentState);

                        const selectionState = getSelectionStateForContent(contentState);

                        [...Object.values(TEXT_COLOR_PRESETS), ...Object.values(TEXT_BACKGROUND_PRESETS)].forEach(
                            (style) => {
                                contentState = Modifier.removeInlineStyle(contentState, selectionState, style);
                            },
                        );

                        newEditorState = EditorState.push(newEditorState, contentState, 'change-inline-style');

                        newData[row][col].value = stringifyCellContent(
                            convertToRaw(newEditorState.getCurrentContent()),
                        );
                    }

                    // If the style needs to be removed, ensure that the styling is removed from Draft format as well
                    if (!shouldAddInlineStyle && textStyle) {
                        const prevContentState = convertFromRaw(content);

                        let newEditorState = EditorState.createWithContent(prevContentState);

                        const selectionState = getSelectionStateForContent(prevContentState);

                        const newContentState = Modifier.removeInlineStyle(prevContentState, selectionState, textStyle);
                        newEditorState = EditorState.push(newEditorState, newContentState, 'change-inline-style');

                        newData[row][col].value = stringifyCellContent(
                            convertToRaw(newEditorState.getCurrentContent()),
                        );
                    }
                }

                setCellMetaObject(row, col, { cellData: newData[row][col] });
            });

            renderAndResize();

            const undoRedoCellSelectionAction = {
                type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                id: elementId,
                cellSelections: hotTableInstanceRef.current.getSelected(),
            };

            dispatchUpdateTableElement({
                id: elementId,
                changes: {
                    tableContent: {
                        colWidthsGU: hotColWidthsGU,
                        data: newData,
                    },
                },

                // On undo/redo, make sure to go to previous cell selections
                batchUndoActions: [undoRedoCellSelectionAction],
                batchRedoActions: [undoRedoCellSelectionAction],
            });
        },
        [
            hotTableInstanceRef,
            getHotCurrentCellSelections,
            getHotColWidthsGU,
            getHotTableData,
            hasInlineStyle,
            setCellMetaObject,
            renderAndResize,
            elementId,
            dispatchUpdateTableElement,
        ],
    );

    // **********************************
    // CELL ALIGNMENT
    // **********************************

    const textAlignmentMenuName = useCallback(
        (textAlignment: TextAlignment) => {
            return (): string => {
                const hotCurrentCellSelections = getHotCurrentCellSelections();

                const isTextAlignmentApplied = !getAllCellsBetween(hotCurrentCellSelections).some(({ row, col }) => {
                    if (!hotTableInstanceRef.current) return false;

                    const { cellData = createTableCellEntry() } = hotTableInstanceRef.current.getCellMeta(row, col);
                    const hotCellValue = hotTableInstanceRef.current.getDataAtCell(row, col); // For formulas, will return formula result

                    return getCellTextAlignment(cellData, locale, hotCellValue) !== textAlignment;
                });

                const label = translateTableContextMenu(`align${capitalize(textAlignment)}`, `Align ${textAlignment}`);
                return `${label}${isTextAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
            };
        },
        [translateTableContextMenu, hotTableInstanceRef, locale, getHotCurrentCellSelections],
    );

    const verticalAlignmentMenuName = useCallback(
        (verticalAlignment: VerticalAlignment) => {
            return (): string => {
                const hotCurrentCellSelections = getHotCurrentCellSelections();

                const isVerticalAlignmentApplied = !getAllCellsBetween(hotCurrentCellSelections).some(
                    ({ row, col }) => {
                        if (!hotTableInstanceRef.current) return false;

                        const { cellData = createTableCellEntry() } = hotTableInstanceRef.current.getCellMeta(row, col);

                        if (!cellData.verticalAlignment)
                            return verticalAlignment !== TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

                        return cellData.verticalAlignment !== verticalAlignment;
                    },
                );

                const label = translateTableContextMenu(
                    `align${capitalize(verticalAlignment)}`,
                    `Align ${verticalAlignment}`,
                );
                return `${label}${isVerticalAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
            };
        },
        [translateTableContextMenu, hotTableInstanceRef, getHotCurrentCellSelections],
    );

    const updateCellTextAlignment = useCallback(
        (textAlignment: TextAlignment) => {
            return (): void =>
                setCellDataForCellSelections(
                    (cellData) => ({ ...cellData, textAlignment }),
                    'CellMeta.updateCellTextAlignment',
                );
        },
        [setCellDataForCellSelections],
    );

    const updateCellVerticalAlignment = useCallback(
        (verticalAlignment: VerticalAlignment) => {
            return (): void =>
                setCellDataForCellSelections(
                    (cellData) => ({ ...cellData, verticalAlignment }),
                    'CellMeta.updateCellVerticalAlignment',
                );
        },
        [setCellDataForCellSelections],
    );

    // **********************************
    // EDIT STATE
    // **********************************

    const jumpToTitleFn = useCallback((): void => {
        showTitle && requestAnimationFrame(() => dispatchStartEditingTitle(elementId));
    }, [elementId, showTitle, dispatchStartEditingTitle]);

    return {
        getHotTableData,
        getHotCurrentCellSelections,
        getHotColWidthsGU,
        renderAndResize,
        popTransactionIdForNextUpdateCellSelection,
        updateHotCellSelections,
        updateCellType,
        updateCellTypeFormat,
        updateColWidthPx,
        updateColWidthsPx,
        addRowsAboveMenuName,
        addRowsAboveDisabled,
        addRowsAbove,
        addRowsBelowMenuName,
        addRowsBelowDisabled,
        addRowsBelow,
        addColsLeftMenuName,
        addColsLeftDisabled,
        addColsLeft,
        addColsRightMenuName,
        addColsRightDisabled,
        addColsRight,
        removeRowsMenuName,
        removeRowsDisabled,
        removeRows,
        removeColsMenuName,
        removeColsDisabled,
        removeCols,
        startEditingCellEditor,
        finishEditingCellEditor,
        addFormulaToCell,
        setCellBackground,
        getCellBackground,
        setTempColor,
        hasInlineStyle,
        applyTextStyle,
        textAlignmentMenuName,
        verticalAlignmentMenuName,
        updateCellTextAlignment,
        updateCellVerticalAlignment,
        jumpToTitle: jumpToTitleFn,
        setCellData,
        setCellDataForCellSelections,
        setCellMetaObject,
    };
};

export default useTableOperations;
